import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    MANAGE_OFFERS_LISTING_KEY,
    MY_CART_DETAILS,
    MY_OFFERS_LISTING_KEY,
    updateCartCache,
    useCartDetails,
    useSelfUpdates,
    useUserSelfDetails,
} from '@steelbuy/data-access';
import { UserRole, CartBuyerModel } from '@steelbuy/ts-shared';
import { toaster } from '@steelbuy/ui-primitive';
import CartReminderNotification from '../components/cart-reminder-notification/CartReminderNotification';
import { ToastReminder } from '../components/toast-reminder/ToastReminder';
import { RoutePath } from '../router/Routes';

const useSelfCacheUpdater = () => {
    const { t } = useTranslation('translation');
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { data: userData } = useUserSelfDetails();
    const { refetch, data: userCache } = useSelfUpdates();
    const previousCount = useRef<null | { buyerCount: number; sellerCount: number }>(null);
    const hasBuyerRole = userData?.roles.includes(UserRole.BUYER) ?? false;

    const showCartReminderToast = (time: number) => {
        if (location.pathname !== RoutePath.MY_CART_CHECKOUT) {
            toaster.warn(
                <CartReminderNotification
                    time={time}
                    cart={queryClient.getQueryData<CartBuyerModel>([MY_CART_DETAILS])}
                />,
                { autoClose: 5000 }
            );
        }
    };

    useCartDetails({ queryOptions: { enabled: false } }, showCartReminderToast);

    const showNewOfferToast = (route: string) =>
        toaster.info(
            <ToastReminder
                i18nKey="application.offers.notifyNewOffer"
                cta={{
                    label: t('application.offers.respondToOffer'),
                    onClick: () => navigate(route),
                }}
            />
        );

    const pathToExclude = [
        RoutePath.CREATE_RFQ_STEP2,
        RoutePath.CREATE_RFQ_STEP3,
        RoutePath.MANAGE_OFFERS_DETAILS,
        RoutePath.SEARCH_LISTING_DETAILS,
        RoutePath.MY_ALERTS_LISTING_DETAILS,
        RoutePath.MY_OFFERS_DETAILS,
        RoutePath.MY_LISTINGS_DETAILS,
        RoutePath.MY_CART_LISTING_DETAILS,
    ];
    useEffect(() => {
        const shouldRefetch = !pathToExclude.some((path) => matchPath({ path, end: true }, location.pathname));
        if (shouldRefetch) {
            refetch();
        }
    }, [location.pathname, refetch]);

    useEffect(() => {
        if (userCache) {
            if (hasBuyerRole) {
                updateCartCache(queryClient, [MY_CART_DETAILS], userCache.cartDetails);
            }
            if (previousCount.current !== null) {
                if (userCache.offers.buyerCount > previousCount.current.buyerCount) {
                    showNewOfferToast(RoutePath.MY_OFFERS);
                    queryClient.removeQueries({ queryKey: [MY_OFFERS_LISTING_KEY] });
                }
                if (userCache.offers.sellerCount > previousCount.current.sellerCount) {
                    showNewOfferToast(RoutePath.MANAGE_OFFERS);
                    queryClient.removeQueries({ queryKey: [MANAGE_OFFERS_LISTING_KEY] });
                }
            }
            previousCount.current = {
                buyerCount: userCache.offers.buyerCount,
                sellerCount: userCache.offers.sellerCount,
            };
        }
    }, [userCache]);
};

export default useSelfCacheUpdater;
